import { Button, DatePicker, Input, Select, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { AddressLocationReviewFiltersInput } from '../graphql/queries/getAddressLocationReviewListing';
import { GET_ALL_ACCOUNTS_FOR_SELECT, GetAllAccountsForSelectResponse } from '../graphql/queries/getAllAccountsForSelect';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getLastWeekDateTimeRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

const { RangePicker } = DatePicker;

export type FilterOptions = {
  address: string;
  trackingNumber: string;
  reporterAccountId: number | undefined;
  reviewerAccountId: number | undefined;
  // - undefined：未初始化（UI 默认展示最近一周）
  // - null：用户手动清空（点击叉号），保持为空
  // - [start, end]：用户选择的时间范围
  createdAtRange?: [Dayjs | null, Dayjs | null] | null;
  reviewStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | 'all';
  reportSource: 'DRIVER' | 'BACKEND' | 'all';
};

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  address: '',
  trackingNumber: '',
  reporterAccountId: undefined,
  reviewerAccountId: undefined,
  createdAtRange: undefined,
  reviewStatus: 'all',
  reportSource: 'all',
};

interface FilterAreaProps {
  filters: FilterOptions;
  handleFilter: (options: FilterOptions) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({ filters, handleFilter }) => {
  const { t } = useTranslation();
  const getDefaultCreatedAtRange = () => {
    const [startDate, endDate] = getLastWeekDateTimeRange();
    return [dayjs(startDate), dayjs(endDate)] as [Dayjs, Dayjs];
  };

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ ...DEFAULT_FILTER_OPTIONS, ...filters });
  const [accountSearchText, setAccountSearchText] = useState<string>('');
  const [reviewerSearchText, setReviewerSearchText] = useState<string>('');
  const hasUserEditedCreatedAtRangeRef = useRef(false);

  useAutoClearDefaultTime({
    filters: filterOptions,
    setFilters: setFilterOptions,
    timeKey: 'createdAtRange',
    hasUserEditedTimeRef: hasUserEditedCreatedAtRangeRef,
  });

  // 当 filters prop 变化时，同步更新内部状态
  useEffect(() => {
    setFilterOptions({ ...DEFAULT_FILTER_OPTIONS, ...filters });
  }, [filters]);

  // 使用统一的样式 Hook
  const labelClassName = useFilterLabelClassName();
  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();

  const labelAddress = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.address'));
  const labelTrackingNumber = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.trackingNumber'));
  const labelReporterAccount = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.reporterAccount'));
  const labelReportedAt = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.reportedAt'));
  const labelStatus = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.status'));
  const labelReportSource = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.reportSource'));
  const labelReviewerAccount = String(t('mapTool.addressLibrary.locationCorrectionPage.filters.reviewerAccount'));
  const col1Labels = [labelAddress, labelStatus, labelReviewerAccount];
  const col2Labels = [labelReporterAccount, labelTrackingNumber];
  const col3Labels = [labelReportedAt, labelReportSource];

  const statusOptions = [
    { value: 'all', label: t('components.input.all') as string },
    { value: 'PENDING', label: t('mapTool.addressLibrary.locationCorrectionPage.status.pending') as string },
    { value: 'APPROVED', label: t('mapTool.addressLibrary.locationCorrectionPage.status.approved') as string },
    { value: 'REJECTED', label: t('mapTool.addressLibrary.locationCorrectionPage.status.rejected') as string },
  ];

  const reportSourceOptions = [
    { value: 'all', label: t('components.input.all') as string },
    { value: 'DRIVER', label: t('mapTool.addressLibrary.locationCorrectionPage.reportSource.driver') as string },
    { value: 'BACKEND', label: t('mapTool.addressLibrary.locationCorrectionPage.reportSource.backend') as string },
  ];

  // 查询上报账号（不限角色：上报人可能是司机，也可能是后台修改坐标的员工）
  const [searchAccounts, { data: accountData, loading: accountLoading }] = useLazyQuery<GetAllAccountsForSelectResponse>(
    GET_ALL_ACCOUNTS_FOR_SELECT,
    {
      fetchPolicy: 'network-only',
    }
  );

  // 防抖搜索，同时处理初始加载
  useEffect(() => {
    const timer = setTimeout(() => {
      if (accountSearchText.trim()) {
        const searchValue = accountSearchText.trim();
        // 同时传递 name 和 phone，后端会进行模糊匹配
        searchAccounts({
          variables: {
            name: searchValue,
            phone: searchValue,
          },
        });
      } else {
        // 如果搜索文本为空，重新查询所有
        searchAccounts({
          variables: {
            name: undefined,
            phone: undefined,
          },
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [accountSearchText, searchAccounts]);

  // 上报账号选项
  const accountOptions = useMemo(() => {
    if (!accountData?.getAllAccountsForSelect) {
      return [];
    }
    return accountData.getAllAccountsForSelect.map((account) => ({
      label: `${account.name}${account.phone ? `(${account.phone})` : ''}`,
      value: Number(account.id),
    }));
  }, [accountData]);

  // 审核人下拉：与上报账号共用同一账号查询接口，但搜索文本与结果各自独立，互不干扰
  const [searchReviewers, { data: reviewerData, loading: reviewerLoading }] = useLazyQuery<GetAllAccountsForSelectResponse>(
    GET_ALL_ACCOUNTS_FOR_SELECT,
    {
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const searchValue = reviewerSearchText.trim();
      searchReviewers({
        variables: {
          name: searchValue || undefined,
          phone: searchValue || undefined,
        },
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [reviewerSearchText, searchReviewers]);

  const reviewerOptions = useMemo(() => {
    if (!reviewerData?.getAllAccountsForSelect) {
      return [];
    }
    return reviewerData.getAllAccountsForSelect.map((account) => ({
      label: `${account.name}${account.phone ? `(${account.phone})` : ''}`,
      value: Number(account.id),
    }));
  }, [reviewerData]);

  const handleReset = () => {
    const resetFilters: FilterOptions = {
      ...DEFAULT_FILTER_OPTIONS,
    };
    hasUserEditedCreatedAtRangeRef.current = false;
    setFilterOptions(resetFilters);
    setAccountSearchText('');
    setReviewerSearchText('');
    handleFilter(resetFilters);
  };

  const handleSearch = () => {
    handleFilter(filterOptions);
  };

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          {/* 第一列 - 固定宽度 33.33% */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col1Labels)}>{labelAddress}</span>
              <div className={inputContainerClassName}>
          <Input
            placeholder={t('mapTool.addressLibrary.locationCorrectionPage.placeholders.address') as string}
            value={filterOptions.address}
            onChange={(e) => setFilterOptions({ ...filterOptions, address: e.target.value })}
            allowClear
                  size="middle"
                  className="w-full"
          />
              </div>
            </div>
        </div>

          {/* 第二列 - 固定宽度 33.33% */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col2Labels)}>{labelReporterAccount}</span>
              <div className={inputContainerClassName}>
          <Select
            showSearch
            allowClear
            placeholder={t('mapTool.addressLibrary.locationCorrectionPage.placeholders.reporterAccount') as string}
            value={filterOptions.reporterAccountId}
            onChange={(value) => {
              const newFilters = { ...filterOptions, reporterAccountId: value };
              setFilterOptions(newFilters);
              // 当清除按钮被点击时（value 为 undefined），重新加载账号数据
              if (value === undefined) {
                setAccountSearchText('');
                searchAccounts({
                  variables: {
                    name: undefined,
                    phone: undefined,
                  },
                });
              }
            }}
            onSearch={setAccountSearchText}
            loading={accountLoading}
            options={accountOptions}
            filterOption={false}
                  size="middle"
                  className="w-full"
            dropdownStyle={{ maxWidth: '400px' }}
            notFoundContent={
              accountLoading
                ? t('mapTool.addressLibrary.locationCorrectionPage.messages.loading')
                : t('mapTool.addressLibrary.locationCorrectionPage.messages.noData')
            }
          />
              </div>
            </div>
        </div>

          {/* 第三列 - 固定宽度 33.33% */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col3Labels)}>{labelReportedAt}</span>
              <div className={inputContainerClassName}>
          <RangePicker
            value={filterOptions.createdAtRange === undefined ? getDefaultCreatedAtRange() : (filterOptions.createdAtRange ?? null)}
            onChange={(dates) => {
              hasUserEditedCreatedAtRangeRef.current = true;
              setFilterOptions({
                ...filterOptions,
                createdAtRange: dates ? (dates as [Dayjs | null, Dayjs | null]) : null,
              });
            }}
                  format="YYYY/MM/DD"
                  size="middle"
                  className="w-full"
          />
              </div>
            </div>
        </div>

          {/* 第二行第一列 - 固定宽度 33.33% */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col1Labels)}>{labelStatus}</span>
              <div className={inputContainerClassName}>
          <Select
            value={filterOptions.reviewStatus}
            onChange={(value) => setFilterOptions({ ...filterOptions, reviewStatus: value })}
            options={statusOptions}
                  size="middle"
                  className="w-full"
          />
        </div>
            </div>
          </div>

          {/* 第二行第二列 - 固定宽度 33.33% */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col2Labels)}>{labelTrackingNumber}</span>
              <div className={inputContainerClassName}>
          <Input
            placeholder={t('mapTool.addressLibrary.locationCorrectionPage.placeholders.trackingNumber') as string}
            value={filterOptions.trackingNumber}
            onChange={(e) => setFilterOptions({ ...filterOptions, trackingNumber: e.target.value })}
            allowClear
                  size="middle"
                  className="w-full"
          />
              </div>
            </div>
          </div>

          {/* 第二行第三列 - 固定宽度 33.33% */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col3Labels)}>{labelReportSource}</span>
              <div className={inputContainerClassName}>
          <Select
            value={filterOptions.reportSource}
            onChange={(value) => setFilterOptions({ ...filterOptions, reportSource: value })}
            options={reportSourceOptions}
                  size="middle"
                  className="w-full"
          />
              </div>
            </div>
          </div>

          {/* 第三行第一列 - 审核人 */}
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(col1Labels)}>{labelReviewerAccount}</span>
              <div className={inputContainerClassName}>
          <Select
            showSearch
            allowClear
            placeholder={t('mapTool.addressLibrary.locationCorrectionPage.placeholders.reviewerAccount') as string}
            value={filterOptions.reviewerAccountId}
            onChange={(value) => {
              setFilterOptions({ ...filterOptions, reviewerAccountId: value });
              // 清除时重置搜索文本，触发防抖查询恢复全量选项
              if (value === undefined) {
                setReviewerSearchText('');
              }
            }}
            onSearch={setReviewerSearchText}
            loading={reviewerLoading}
            options={reviewerOptions}
            filterOption={false}
                  size="middle"
                  className="w-full"
            dropdownStyle={{ maxWidth: '400px' }}
            notFoundContent={
              reviewerLoading
                ? t('mapTool.addressLibrary.locationCorrectionPage.messages.loading')
                : t('mapTool.addressLibrary.locationCorrectionPage.messages.noData')
            }
          />
              </div>
            </div>
          </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button onClick={handleReset} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.reset')}</span>
        </Button>
        <Button type="primary" onClick={handleSearch} className="w-auto min-w-24">
          <span className="font-bold">{t('components.button.filter')}</span>
        </Button>
      </div>
    </div>
  );
};

export const convertToGraphQLFilters = (options: FilterOptions): AddressLocationReviewFiltersInput => {
  const graphQLFilters: AddressLocationReviewFiltersInput = {};

  if (options.address) {
    graphQLFilters.address = options.address;
  }

  // 运单号模糊匹配，去掉首尾空格后非空才传
  if (options.trackingNumber && options.trackingNumber.trim()) {
    graphQLFilters.trackingNumber = options.trackingNumber.trim();
  }

  if (options.reporterAccountId !== undefined && options.reporterAccountId !== null) {
    graphQLFilters.reporterAccountId = options.reporterAccountId;
  }

  if (options.reviewerAccountId !== undefined && options.reviewerAccountId !== null) {
    graphQLFilters.reviewerAccountId = options.reviewerAccountId;
  }

  // createdAtRange：只有在用户提供了完整范围时才传给后端
  if (options.createdAtRange && Array.isArray(options.createdAtRange)) {
    const [start, end] = options.createdAtRange;
    if (start && end) {
      graphQLFilters.createdAtRange = {
        start: start.startOf('day').toISOString(),
        end: end.endOf('day').toISOString(),
      };
    }
  }

  if (options.reviewStatus && options.reviewStatus !== 'all') {
    graphQLFilters.reviewStatus = options.reviewStatus;
  }

  if (options.reportSource && options.reportSource !== 'all') {
    graphQLFilters.reportSource = options.reportSource;
  }

  return graphQLFilters;
};

export default FilterArea;

